import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";

import {
  ModalProvider,
  formatNumber,
  generateRandomId,
  getCurrentReportingValues,
  isFieldEnrolled,
  isFieldWithoutPayoutDueToNoPoints,
  useBulkDeleteFieldCropPractices,
  useDeleteField,
  useFeatureFlag,
  useGetFarmReportingYear,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useGetVerificationEditingRequests,
  useTranslation,
  useUserContext,
} from "utils";

import { FEATURE_FLAGS, getUnitDisplayLabel } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AllCropsPracticeDataByYear,
  AllCropsPracticeDataItem,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CropFCPO,
  FieldListField,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeFCPO,
  PracticeSlug,
  VIEW_TYPE,
  ZERO_UUID,
} from "models";

import {
  BulkActionBar,
  Button,
  Checkbox,
  CounterBadge,
  Drawer,
  Flex,
  Icon,
  Modal,
  MultiCropSelectionBarMissingData,
  Table,
  TagNormal,
  Tooltip,
  Typography,
  Message as message,
} from "components";

import { AddEditCropDrawer } from "pages/app/all-field-view/components/AddEditCropDrawer";
import { BulkEditCrop, BulkEditCropDrawer } from "pages/app/all-field-view/components/BulkEditCropDrawer";
import { DATA_STATUS } from "pages/app/all-field-view/components/FieldFilterOptions";
import { NoMatchesFeedback } from "pages/app/all-field-view/components/NoMatchesFeedback";
import { CALENDAR_YEAR_END, CALENDAR_YEAR_START, NEW_CROP } from "pages/app/all-field-view/constants/cropForm";
import {
  getCropMissingDataRules,
  getCropYearFromDates,
  getEnrolledFields,
  getFieldMandatoryYears,
  hasOpenCropEditingRequest,
  hasOpenPracticeEditingRequest,
  narrowSelectionToSkippedCrops,
} from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { CROP_TYPE_OPTIONS, getFieldsWithoutCropType } from "pages/app/all-field-view/utils/CropTypeUtils";
import { AddCropTypeDrawer } from "pages/app/all-fields-data-table-view/components/AddCropTypeDrawer";
import { getAllFieldsDataTableColumns } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTableColumns";
import { type AllFieldsDataTableFiltersState } from "pages/app/all-fields-data-table-view/components/AllFieldsDataTableFilters";
import { TableFieldCell } from "pages/app/all-fields-data-table-view/components/TableFieldCell";
import { useMissingPracticesHighlight } from "pages/app/all-fields-data-table-view/hooks/useMissingPracticesHighlight";
import {
  buildCopyToPlaceholdersData,
  buildMissingDataForm,
  filterAndSortEntries,
  getAddCropYearWindow,
  getAllFieldsDataTableRowApplicability,
  getAllFieldsDataTableRowCompleteness,
  getSelectAllMatchingItems,
  isCropSelectionCompatible,
  isMineralFertilizerOfferedByProgram,
  isPracticeMandatoryForYear,
  isSelectAllFullySelected,
} from "pages/app/all-fields-data-table-view/utils/AllFieldsDataTableUtils";
import { getFieldBaselineYearRange } from "pages/app/field-timeline/utils/PracticeUtils";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";
import { FieldEnrollmentDrawer } from "pages/app/fields/components/FieldEnrollmentDrawer";
import {
  AddEditMissingDataDrawer,
  MissingDataDrawerPracticeType,
  MissingDataForm,
  YIELD_PSEUDO_PRACTICE_TYPE,
} from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { BulkDataDrawer } from "pages/app/historical-practice-data-table-view/components/BulkDataDrawer.tsx";
import { PracticeChip } from "pages/app/historical-practice-data-table-view/components/PracticeChip";
import { PracticeColumn } from "pages/app/historical-practice-data-table-view/components/PracticeColumn";
import { PracticeTableTooltip } from "pages/app/historical-practice-data-table-view/components/PracticeTableTooltip";
import { useMissingDataTableSelection } from "pages/app/historical-practice-data-table-view/hooks/useMissingDataTableSelection";
import {
  BULK_CROP_DATA_POINT,
  BULK_DATA_DRAWER_MODE,
  type BulkDataDrawerMode,
  type BulkDataPointType,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

type AllFieldsDataTableProps = {
  allCropsPracticeDataByYear: AllCropsPracticeDataByYear;
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined;
  carbonProgram: CarbonProgram | null;
  selectedYear: number | undefined;
  fields: FieldListField[] | undefined;
  filters: AllFieldsDataTableFiltersState;
  dataStatusFilter: DATA_STATUS;
  tableBodyHeight: number;
  hasFiltersActive?: boolean;
  onClearFilters?: () => void;
  farmId: string | undefined;
  totalFieldsCount: number;
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | undefined;
  isDataSubmitted: boolean;
  isDataSubmissionReopened: boolean;
  lastSubmissionDate: string | null | undefined;
  lastSubmissionYear: number | null | undefined;
  fieldSizes: Record<string, number>;
  allFieldsTotalHectares: number | null;
  isEnrollmentDataLoading?: boolean;
};

const isSameField = (entryA: AllCropsPracticeDataItem | undefined, entryB: AllCropsPracticeDataItem | undefined) => entryA?.fieldId === entryB?.fieldId;

const getGroupedRowProps = (record: object) => ({
  className: "isFollowedBySameFieldRow" in record && record.isFollowedBySameFieldRow ? "all-fields-data-table-view-grouped-row" : "",
});

const AllFieldsDataTableComponent = ({
  allCropsPracticeDataByYear,
  allFieldsPractices,
  carbonProgram,
  selectedYear,
  fields,
  filters,
  dataStatusFilter,
  tableBodyHeight,
  hasFiltersActive,
  onClearFilters,
  farmId,
  totalFieldsCount,
  carbonProgramOutcomes,
  isDataSubmitted,
  isDataSubmissionReopened,
  lastSubmissionDate,
  lastSubmissionYear,
  fieldSizes,
  allFieldsTotalHectares,
  isEnrollmentDataLoading,
}: AllFieldsDataTableProps) => {
  const { t, i18n } = useTranslation(["allFieldsDataTableView", "historicalPracticeDataTableView", "practices", "allFieldView", "farm", "fieldTimeline"]);
  const cropLabel = useCallback(
    (slug: string, fallback: string) => (i18n.exists(`practices:crops.${slug}`) ? t(`practices:crops.${slug}`) : fallback),
    [t, i18n]
  );
  const { user } = useUserContext();
  const {
    data: { activeFarmReportingYear, isReportingYearLocked },
  } = useGetFarmReportingYear(user?.farm.id || null);
  const { data: verificationEditingRequests } = useGetVerificationEditingRequests({ farmId: farmId ?? "" });

  const [showDrawerMissingDataDrawer, setShowDrawerMissingDataDrawer] = useState<boolean>(false);
  const [drawerItems, setDrawerItems] = useState<MissingDataForm[]>([]);
  const [drawerShouldValidateOnMount, setDrawerShouldValidateOnMount] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<MissingDataDrawerPracticeType>();
  const [isBulkDrawerShown, setIsBulkDrawerShown] = useState<boolean>(false);
  const [bulkDrawerMode, setBulkDrawerMode] = useState<BulkDataDrawerMode>(BULK_DATA_DRAWER_MODE.edit);
  const [addGrowingPeriodEnabled, setAddGrowingPeriodEnabled] = useState<boolean>(false);
  const [addSeedingEnabled, setAddSeedingEnabled] = useState<boolean>(false);
  const [addTillageEnabled, setAddTillageEnabled] = useState<boolean>(false);
  const [addOrganicFertilizerEnabled, setAddOrganicFertilizerEnabled] = useState<boolean>(false);
  const [addMineralFertilizerEnabled, setAddMineralFertilizerEnabled] = useState<boolean>(false);
  const [addYieldEnabled, setAddYieldEnabled] = useState<boolean>(false);
  const [fieldIdsToEnroll, setFieldIdsToEnroll] = useState<string[] | null>(null);
  const [showDeleteCropsModal, setShowDeleteCropsModal] = useState<boolean>(false);
  const [fieldToEdit, setFieldToEdit] = useState<FieldListField | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<FieldListField | null>(null);
  // one AddCropTypeDrawer instance for both the single-field "+Add crop" action and the bulk "Add crops" action —
  // its onContinue hands back only the fields eligible for the chosen crop type/year, so this branches into
  // AddEditCropDrawer (one field) or BulkEditCropDrawer (several) rather than needing two separate pickers
  const [fieldsForAddCropFlow, setFieldsForAddCropFlow] = useState<FieldListField[] | null>(null);
  // separate from fieldsForAddCropFlow so "Back" can re-show the picker without resetting its selection
  const [showAddCropTypeDrawer, setShowAddCropTypeDrawer] = useState(false);
  const [singleCropToAdd, setSingleCropToAdd] = useState<{
    field: FieldListField;
    crop: CropFCPO;
    year: number;
    // empty for a brand-new crop; the crop's own assigned practices when viewing/editing one that already exists
    existingTillages: PracticeFCPO[];
    existingFertilizers: PracticeFCPO[];
  } | null>(null);
  const [cropsToAdd, setCropsToAdd] = useState<BulkEditCrop[] | null>(null);
  // "copy 1 real crop's data to N placeholders": the source crop feeds BulkEditCropForm's prefill only, the
  // placeholders (built from its values) are the only crops actually written on save
  const [copyTemplateCrop, setCopyTemplateCrop] = useState<BulkEditCrop | null>(null);
  const [cropsToCopyToPlaceholders, setCropsToCopyToPlaceholders] = useState<BulkEditCrop[] | null>(null);
  // source crop's own tillage/fertilizer practices, so those columns default to its values instead of blank
  const [copySourceTillages, setCopySourceTillages] = useState<PracticeFCPO[]>([]);
  const [copySourceFertilizers, setCopySourceFertilizers] = useState<PracticeFCPO[]>([]);

  const { isEnabled: isBulkEditingImprovementsEnabled } = useFeatureFlag(FEATURE_FLAGS.BULK_EDITING_IMPROVEMENTS);
  const { mutate: deleteField } = useDeleteField();
  const { mutateAsync: deleteFieldCropPractices } = useBulkDeleteFieldCropPractices({ farmId: user?.farm.id ?? undefined });

  const { data: seedingPractices } = useGetPracticeSeedings({ enabled: false });
  const { data: tillagePractices } = useGetPracticeTillages({ enabled: false });
  const { data: organicFertilizerPractices } = useGetPracticeOrganicFertilizers({ enabled: false });
  const { data: mineralFertilizerPractices } = useGetPracticeMineralFertilizers({ enabled: false });
  const { data: practiceCrops } = useGetPracticeCrops({});

  const openAddCropTypeDrawer = (fields: FieldListField[]) => {
    setFieldsForAddCropFlow(fields);
    setShowAddCropTypeDrawer(true);
  };

  const onAddMissingItemClicked = (missingDataForm: MissingDataForm, practiceType: MissingDataDrawerPracticeType, shouldValidateOnMount: boolean) => {
    setDrawerItems([missingDataForm]);
    setSelectedType(practiceType);
    setDrawerShouldValidateOnMount(shouldValidateOnMount);
    // a cell click is about that one value — the bulk drawer's data point cards belong to the selection flow
    setIsBulkDrawerShown(false);
    setShowDrawerMissingDataDrawer(true);
  };

  // the page passes `fields` pre-sorted per the (unchanged) FieldSortOptions selection — rows are ordered by their
  // field's position in that array so the table respects the applied sort without re-implementing it here
  const fieldOrderIndex = useMemo(() => new Map((fields ?? []).map((field, index) => [field.id, index])), [fields]);

  const filteredEntries = useMemo(() => {
    if (!selectedYear) return [];
    const entries = allCropsPracticeDataByYear[selectedYear] ?? [];
    return filterAndSortEntries(entries, {
      fieldOrderIndex,
      filters,
      dataStatusFilter,
      carbonProgram,
      practiceCrops,
      allFieldsPractices,
      activeFarmReportingYear,
      selectedYear,
      verificationEditingRequests,
    });
  }, [
    selectedYear,
    allCropsPracticeDataByYear,
    filters,
    dataStatusFilter,
    carbonProgram,
    practiceCrops,
    allFieldsPractices,
    activeFarmReportingYear,
    fieldOrderIndex,
    verificationEditingRequests,
  ]);

  // computed once per year rather than re-scanned per crop row below
  const fieldIdsWithMainCropThisYear = useMemo(() => {
    const entries = selectedYear ? allCropsPracticeDataByYear[selectedYear] ?? [] : [];
    return new Set(entries.filter((entry) => entry.crop.cropType === CROP_TYPE_FCPO.MAIN).map((entry) => entry.fieldId));
  }, [selectedYear, allCropsPracticeDataByYear]);

  const buildMissingItem = useCallback(
    (entry: AllCropsPracticeDataItem): MissingDataForm =>
      buildMissingDataForm(entry, {
        activeFarmReportingYear,
        isReportingYearLocked,
        selectedYear,
        carbonProgram,
        availablePracticeBundles: allFieldsPractices?.[entry.fieldId]?.availablePracticeBundles ?? [],
        carbonProgramOutcomesForField: carbonProgramOutcomes?.[entry.fieldId],
        isDataSubmitted,
        lastSubmissionDate,
        lastSubmissionYear,
        verificationEditingRequests,
      }),
    [
      activeFarmReportingYear,
      isReportingYearLocked,
      selectedYear,
      carbonProgram,
      allFieldsPractices,
      carbonProgramOutcomes,
      isDataSubmitted,
      lastSubmissionDate,
      lastSubmissionYear,
      verificationEditingRequests,
    ]
  );

  // crop filters have nothing to filter by when there's no crop data at all, even if fields exist — a placeholder
  // (zero crop entries) is always "missing", so it matches the Missing status filter but never Completed
  const hasFiltersIncompatibleWithPlaceholders =
    filters.appliedCropIds.length > 0 || filters.previouslyAppliedCropIds.length > 0 || dataStatusFilter === DATA_STATUS.COMPLETED;

  // fields with zero crop entries for the year — always addable as a Main crop, hence one placeholder slot each.
  // Non-enrolled fields don't count as "missing" though, matching the isEnrolled gate used elsewhere on this page.
  const placeholderFields = useMemo(() => {
    if (!selectedYear || hasFiltersIncompatibleWithPlaceholders) return [];
    const fieldIdsWithCropThisYear = new Set((allCropsPracticeDataByYear[selectedYear] ?? []).map((entry) => entry.fieldId));
    const requiresEnrollment = dataStatusFilter === DATA_STATUS.MISSING;
    return (fields ?? []).filter(
      (field) =>
        !fieldIdsWithCropThisYear.has(field.id) &&
        (!requiresEnrollment || isFieldEnrolled(allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [], carbonProgram))
    );
  }, [selectedYear, hasFiltersIncompatibleWithPlaceholders, allCropsPracticeDataByYear, fields, dataStatusFilter, allFieldsPractices, carbonProgram]);

  const getPlaceholderKey = (fieldId: string) => `placeholder-${fieldId}`;

  const buildPlaceholderMissingItem = useCallback(
    (field: FieldListField): MissingDataForm => ({
      key: getPlaceholderKey(field.id),
      fieldId: field.id,
      year: selectedYear ?? moment().year(),
      crop: { ...NEW_CROP(CROP_TYPE_FCPO.MAIN), id: getPlaceholderKey(field.id) },
      isPlaceholder: true,
      seedings: null,
      tillages: null,
      organicFertilizers: null,
      mineralFertilizers: null,
      isGrowingPeriodEditable: false,
      isSeedingEditable: false,
      isOrganicEditable: false,
      isTillageEditable: false,
      isTillageDepthEditable: false,
      areTillageDatesEditable: false,
      areOrganicDatesEditable: false,
    }),
    [selectedYear]
  );

  const placeholderItemsByKey = useMemo(
    () => new Map(placeholderFields.map((field) => [getPlaceholderKey(field.id), buildPlaceholderMissingItem(field)])),
    [placeholderFields, buildPlaceholderMissingItem]
  );

  const getMissingItemById = useCallback(
    (id: string): MissingDataForm | null => {
      const entry = filteredEntries.find((item) => item.crop.id === id);
      if (entry) return buildMissingItem(entry);
      return placeholderItemsByKey.get(id) ?? null;
    },
    [filteredEntries, buildMissingItem, placeholderItemsByKey]
  );

  // sorted by field order, not real-then-placeholder, so shift-click range-select matches the rendered order
  const selectableRowKeys = useMemo(() => {
    const fieldIds = new Set((fields ?? []).map((field) => field.id));
    const realKeys = filteredEntries.filter((entry) => fieldIds.has(entry.fieldId)).map((entry) => ({ key: entry.crop.id, fieldId: entry.fieldId }));
    const placeholderKeys = placeholderFields.map((field) => ({ key: getPlaceholderKey(field.id), fieldId: field.id }));
    return [...realKeys, ...placeholderKeys]
      .sort((a, b) => (fieldOrderIndex.get(a.fieldId) ?? 0) - (fieldOrderIndex.get(b.fieldId) ?? 0))
      .map(({ key }) => key);
  }, [filteredEntries, fields, placeholderFields, fieldOrderIndex]);

  // refs, not state: isRowSelectable feeds the hook that owns selectedItems, so it can't close over state directly
  const selectedCropTypeRef = useRef<CROP_TYPE_FCPO | undefined>(undefined);
  // fallback lock for "select all" when nothing is selected yet
  const firstSelectableCropTypeRef = useRef<CROP_TYPE_FCPO | undefined>(undefined);
  // a placeholder may join a selection alongside at most one real crop (its data can then be copied onto the
  // placeholders); a second real crop may only join once no placeholder is selected
  const realCropSelectedCountRef = useRef(0);
  const hasPlaceholderSelectedRef = useRef(false);
  const firstSelectableItem = getMissingItemById(selectableRowKeys[0]);
  firstSelectableCropTypeRef.current = firstSelectableItem?.crop?.cropType;
  const isRowSelectable = useCallback(
    (item: MissingDataForm) =>
      isCropSelectionCompatible(item, {
        lockedCropType: selectedCropTypeRef.current ?? firstSelectableCropTypeRef.current,
        realCropSelectedCount: realCropSelectedCountRef.current,
        hasPlaceholderSelected: hasPlaceholderSelectedRef.current,
      }),
    []
  );

  const { selectedItems, setSelectedItems, isMultiSelectMode, setIsMultiSelectMode, onCheckBoxClicked, selectedRowCount } = useMissingDataTableSelection({
    selectableRowKeys,
    getMissingItemById,
    selectedYear,
    isRowSelectable,
  });

  const handleSelectAllOfFirstType = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedItems(new Map());
        setIsMultiSelectMode(false);
        return;
      }

      const matchingItems = getSelectAllMatchingItems(selectableRowKeys, getMissingItemById, selectedItems);
      setSelectedItems(matchingItems);
      setIsMultiSelectMode(matchingItems.size > 0);
    },
    [getMissingItemById, selectableRowKeys, selectedItems, setSelectedItems, setIsMultiSelectMode]
  );

  // one toggle per practice column instead of per-row: expanding Tillage, say, shows every row's extra tillage
  // entries without affecting the other columns
  const [expandedPracticeColumns, setExpandedPracticeColumns] = useState<Partial<Record<PracticeSlug, boolean>>>({});
  const toggleExpandColumn = useCallback(
    (practiceSlug: PracticeSlug) => setExpandedPracticeColumns((previous) => ({ ...previous, [practiceSlug]: !previous[practiceSlug] })),
    []
  );

  const { highlightedMissingRowKey, toggleHighlightMissingPractices } = useMissingPracticesHighlight();

  // derived once per selectedItems change, in a single pass — placeholders vs real crops, and the crop type
  // shared across the selected REAL crops only (placeholders are always Main-shaped, so they're excluded rather
  // than forcing every selected placeholder to also "match" a Cover/Undersowing pick)
  const { isPlaceholderOnlySelection, realCropSelectedCount, hasPlaceholderSelected, selectedPlaceholderItems, singleSelectedRealCropItem, selectedCropType } =
    useMemo(() => {
      const items = [...selectedItems.values()];
      const placeholderItems = items.filter((item) => item.isPlaceholder);
      const realCropItems = items.filter((item) => !item.isPlaceholder);
      const realCropTypes = new Set(realCropItems.map((item) => item.crop?.cropType).filter(Boolean));
      return {
        isPlaceholderOnlySelection: items.length > 0 && placeholderItems.length === items.length,
        realCropSelectedCount: realCropItems.length,
        hasPlaceholderSelected: placeholderItems.length > 0,
        selectedPlaceholderItems: placeholderItems,
        // the one real crop whose data can be copied onto the selected placeholders, when exactly one is selected
        singleSelectedRealCropItem: realCropItems.length === 1 ? realCropItems[0] : undefined,
        selectedCropType: realCropTypes.size === 1 ? [...realCropTypes][0] : undefined,
      };
    }, [selectedItems]);

  useEffect(() => {
    selectedCropTypeRef.current = selectedCropType;
    realCropSelectedCountRef.current = realCropSelectedCount;
    hasPlaceholderSelectedRef.current = hasPlaceholderSelected;
  }, [selectedCropType, realCropSelectedCount, hasPlaceholderSelected]);
  const isCopyToPlaceholdersSelection = !!singleSelectedRealCropItem && selectedPlaceholderItems.length > 0;

  // per-row disabled state — unlike isRowSelectable, no first-row fallback, or every Cover/Undersowing row would
  // render disabled with nothing selected yet
  const isItemLockedOut = useCallback(
    (item: MissingDataForm) => !isCropSelectionCompatible(item, { lockedCropType: selectedCropType, realCropSelectedCount, hasPlaceholderSelected }),
    [selectedCropType, realCropSelectedCount, hasPlaceholderSelected]
  );

  const isSelectAllChecked = useMemo(
    () => isSelectAllFullySelected(selectableRowKeys, getMissingItemById, selectedItems, selectedRowCount),
    [selectableRowKeys, getMissingItemById, selectedItems, selectedRowCount]
  );

  const hasAnyMineralFertilizerDataForYear = useMemo(() => {
    const entriesForYear = selectedYear ? allCropsPracticeDataByYear[selectedYear] ?? [] : [];
    return entriesForYear.some((entry) => (entry.assignedPractices[PRACTICE_SLUG.MINERAL_FERTILIZERS]?.length ?? 0) > 0);
  }, [selectedYear, allCropsPracticeDataByYear]);

  const showMineralFertilizers = isMineralFertilizerOfferedByProgram(carbonProgram) || hasAnyMineralFertilizerDataForYear;
  // inventory programs and no-program farms have no baseline concept, so the column is dropped entirely
  const showBaselineColumn = !!carbonProgram && carbonProgram.accountingMethodology !== ACCOUNTING_METHODOLOGY.inventory;

  // the bulk drawer's data point cards mirror this view's columns, so a practice with no column offers no card either
  const availablePracticeTypes = useMemo<BulkDataPointType[]>(
    () => [
      BULK_CROP_DATA_POINT.cropName,
      PRACTICE_SLUG.CROPS,
      YIELD_PSEUDO_PRACTICE_TYPE,
      PRACTICE_SLUG.SEEDINGS,
      BULK_CROP_DATA_POINT.residue,
      PRACTICE_SLUG.TILLAGES,
      PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      ...(showMineralFertilizers ? [PRACTICE_SLUG.MINERAL_FERTILIZERS] : []),
    ],
    [showMineralFertilizers]
  );

  // reuses getFieldsWithoutCropType so Cover crops are handled the same as everywhere else (one of each type
  // present doesn't mean "used up", since another non-overlapping Cover crop may still fit)
  const fieldIdsWithAllCropTypesUsed = useMemo(() => {
    if (!selectedYear || !fields) return new Set<string>();
    const fieldIdsStillEligibleForSomeType = new Set(
      CROP_TYPE_OPTIONS.flatMap((cropType) =>
        getFieldsWithoutCropType({ fields, fieldPractices: allFieldsPractices ?? {}, year: selectedYear, cropType }).map((field) => field.id)
      )
    );
    return new Set(fields.filter((field) => !fieldIdsStillEligibleForSomeType.has(field.id)).map((field) => field.id));
  }, [selectedYear, fields, allFieldsPractices]);

  const addCropYearRange = useMemo(
    () =>
      activeFarmReportingYear === undefined ? undefined : getAddCropYearWindow(activeFarmReportingYear, carbonProgram?.fieldsManagement.monitoringPeriodType),
    [activeFarmReportingYear, carbonProgram?.fieldsManagement.monitoringPeriodType]
  );

  const addCropYearOptions = useMemo(() => {
    if (!addCropYearRange) return [];
    const { startYear, endYear } = addCropYearRange;
    return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
  }, [addCropYearRange]);

  const addCropDisabledReason = useMemo(() => {
    if (selectedYear === undefined || !addCropYearRange) return undefined;
    const { startYear, endYear } = addCropYearRange;
    if (selectedYear >= startYear && selectedYear <= endYear) return undefined;
    return t("farm:contextMenu.addCropYearOutsideAllowedRangeHelperText", { startYear, endYear });
  }, [selectedYear, addCropYearRange, t]);

  // baseline is a fixed field-level property, so it renders the same whether the row is a placeholder or not
  const renderBaselineCell = useCallback(
    (field: FieldListField) => {
      const isEnrolled = isFieldEnrolled(allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [], carbonProgram);
      const baselineYearRange = isEnrolled ? getFieldBaselineYearRange(field.baselineYears ?? []) : null;

      return baselineYearRange ? (
        <Tooltip variant="neutral" title={t("allFieldsDataTableView:table.data.baselineTooltip", baselineYearRange)}>
          <TagNormal className="baseline-chip chip-pill capitalize" text={t("allFieldsDataTableView:table.data.baselineRange", baselineYearRange)} />
        </Tooltip>
      ) : (
        <Typography variant="paragraph-s" color="grey-600" className="margin-0">
          {t("allFieldsDataTableView:table.data.baselineNotSet")}
        </Typography>
      );
    },
    [allFieldsPractices, carbonProgram, t]
  );

  const tableData = useMemo(
    () =>
      selectedYear
        ? filteredEntries
            .map((entry, index) => {
              const field = fields?.find((field) => field.id === entry.fieldId);
              if (!field) return null;

              // filteredEntries is sorted by field order, so a field's crops are always adjacent
              const isFirstRowForField = !isSameField(filteredEntries[index - 1], entry);
              const isFollowedBySameFieldRow = isSameField(filteredEntries[index + 1], entry);
              // the sub-row directly following the field's own Main row — the connector's trunk reaches up
              // differently here than between two stacked sub-rows, since the Main row's own geometry differs
              const isFirstSubRowForField = !isFirstRowForField && !isSameField(filteredEntries[index - 2], entry);

              const { crop, assignedPractices } = entry;
              const seedings = assignedPractices[PRACTICE_SLUG.SEEDINGS];
              const tillages = assignedPractices[PRACTICE_SLUG.TILLAGES];
              const organicFertilizers = assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS];
              const mineralFertilizers = assignedPractices[PRACTICE_SLUG.MINERAL_FERTILIZERS];
              const hasOpenEditingRequest =
                hasOpenCropEditingRequest(verificationEditingRequests ?? [], crop.id) ||
                hasOpenPracticeEditingRequest(verificationEditingRequests ?? [], {
                  seedingPracticeId: crop.seedingPracticeId,
                  practiceIds: [...(tillages ?? []), ...(organicFertilizers ?? []), ...(mineralFertilizers ?? [])]
                    .map((practice) => practice.id)
                    .filter((id): id is string => !!id),
                });
              const hasExistingMineralFertilizerData = (mineralFertilizers?.length ?? 0) > 0;
              const hasExistingYieldValue = crop.yieldPerHa != null;
              const cropDetails = practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.cropId);
              const applicability = getAllFieldsDataTableRowApplicability(crop, carbonProgram, hasExistingMineralFertilizerData, hasExistingYieldValue);
              // checked against the crop's own reference year (matching Timeline), not selectedYear, since a Cover/Undersowing
              // crop can be rebucketed to a different display year than the one its mandatory-year status is keyed to
              const fieldPracticeBundles = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
              const cropYear = getCropYearFromDates(crop.cropType, moment(crop.seedingStartDate), moment(crop.harvestEndDate)) ?? selectedYear;
              const missingDataRules = getCropMissingDataRules(getFieldMandatoryYears(fieldPracticeBundles, carbonProgram, activeFarmReportingYear), cropYear);
              const { isBeforeMandatoryYears } = missingDataRules;
              const completeness = getAllFieldsDataTableRowCompleteness(entry, carbonProgram, cropDetails, missingDataRules.requiredPractices);
              // a non-enrolled field has nothing at stake for the program, so there's nothing to nag about —
              // matches Timeline, which suppresses the same missing-data indicators for it
              const isEnrolled = isFieldEnrolled(fieldPracticeBundles, carbonProgram);
              const isMissingDataTracked = isEnrolled && !isBeforeMandatoryYears;
              // mineral fertilizer is optional and excluded from completeness.isComplete above, but once a field has
              // opted into the bundle, Timeline treats it as required for the years that opt-in covers
              const isMineralFertilizerRequired = isMissingDataTracked && missingDataRules.isMineralFertilizerRequired;
              // a cover/undersowing crop can be fully complete on its own and still leave the field incomplete
              // overall while no main crop has been added for the year — count that as one more missing item
              // rather than showing this row as "done"
              const isMissingMainCropForField = isMissingDataTracked && crop.cropType !== CROP_TYPE_FCPO.MAIN && !fieldIdsWithMainCropThisYear.has(field.id);
              const missingRequiredPracticesCount =
                (!isMissingDataTracked
                  ? 0
                  : [
                      completeness.growingPeriod,
                      completeness.seedings,
                      completeness.tillages,
                      completeness.organicFertilizers,
                      completeness.yield,
                      ...(isMineralFertilizerRequired ? [completeness.mineralFertilizers] : []),
                    ].filter((isPracticeComplete) => !isPracticeComplete).length) +
                (isMissingMainCropForField ? 1 : 0) +
                (hasOpenEditingRequest ? 1 : 0);
              // keys match the row's own column keys, so AllFieldsDataTableColumns can look up a cell's own key
              const missingPracticeColumns: Partial<Record<string, boolean>> = {
                growingPeriod: isMissingDataTracked && !completeness.growingPeriod,
                [PRACTICE_SLUG.SEEDINGS]: isMissingDataTracked && !completeness.seedings,
                [PRACTICE_SLUG.TILLAGES]: isMissingDataTracked && !completeness.tillages,
                [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: isMissingDataTracked && !completeness.organicFertilizers,
                [PRACTICE_SLUG.MINERAL_FERTILIZERS]: isMineralFertilizerRequired && !completeness.mineralFertilizers,
                yield: isMissingDataTracked && !completeness.yield,
              };

              const isSelectedRow = selectedItems.has(entry.crop.id);

              const missingItem = buildMissingItem(entry);

              // the mineral fertilizer drawer saves through the generic single-crop endpoint, which validates and
              // replaces the crop's entire practices payload — so it rejects the save if the crop's own Growing
              // Period is incomplete, or if an existing tillage/organic fertilizer entry is still open-ended
              const hasIncompleteGrowingPeriod = !crop.seedingStartDate || !crop.harvestEndDate;
              const hasOpenEndedPractice = [...(tillages ?? []), ...(organicFertilizers ?? [])].some(
                (practice) => practice.assignedPracticeId !== ZERO_UUID && practice.endDate === null
              );
              const isMineralFertilizerSaveBlocked = hasIncompleteGrowingPeriod || hasOpenEndedPractice;
              const canAddAnotherCropType = !fieldIdsWithAllCropTypesUsed.has(field.id);
              const isCheckboxDisabledByCropTypeLock = !isSelectedRow && isItemLockedOut(missingItem);

              const isCropNameLocked = !missingItem.isCropNameEditable;
              const openCropDetails = () =>
                setSingleCropToAdd({
                  field,
                  crop,
                  year: crop.year ?? selectedYear!,
                  existingTillages: tillages ?? [],
                  existingFertilizers: [...(organicFertilizers ?? []), ...(mineralFertilizers ?? [])],
                });

              const handleStatusClick = isMissingMainCropForField
                ? () => openAddCropTypeDrawer([field])
                : missingRequiredPracticesCount > 0
                ? hasOpenEditingRequest
                  ? openCropDetails
                  : () => toggleHighlightMissingPractices(entry.crop.id)
                : undefined;

              const naCell = (
                <Typography variant="paragraph-s" className="margin-0">
                  {t("historicalPracticeDataTableView:table.data.na")}
                </Typography>
              );

              const baseline = renderBaselineCell(field);

              return {
                key: entry.crop.id,
                fieldId: entry.fieldId,
                isFollowedBySameFieldRow,
                isCheckboxDisabledByCropTypeLock,
                missingPracticeColumns,
                baseline,
                select: (
                  <Flex align="center" justify="flex-start" className="all-fields-data-table-view-select-checkbox-wrapper" data-no-row-select>
                    <Checkbox
                      className="historical-practice-data-table-view-select-checkbox"
                      checked={isSelectedRow}
                      disabled={isCheckboxDisabledByCropTypeLock}
                      onChange={(e) => onCheckBoxClicked(entry.crop.id, !!e.nativeEvent.shiftKey)}
                    />
                  </Flex>
                ),
                field: (
                  <Flex className="all-fields-data-table-view-clickable-cell" onClick={openCropDetails}>
                    <TableFieldCell
                      field={field}
                      carbonProgram={carbonProgram}
                      availablePracticeBundles={allFieldsPractices?.[field.id]?.availablePracticeBundles ?? []}
                      hasNoPayoutDueToNoPoints={isFieldWithoutPayoutDueToNoPoints(carbonProgramOutcomes?.[field.id], activeFarmReportingYear)}
                      cropType={crop.cropType}
                      year={selectedYear}
                      activeFarmReportingYear={activeFarmReportingYear}
                      isSubRow={!isFirstRowForField}
                      isFirstCropForField={isFirstSubRowForField}
                      isLastCropForField={!isFollowedBySameFieldRow}
                      onAddCrop={canAddAnotherCropType ? () => openAddCropTypeDrawer([field]) : undefined}
                      addCropDisabledReason={addCropDisabledReason}
                      hasAnyRowSelected={selectedRowCount > 0}
                      onEditField={setFieldToEdit}
                      onDeleteField={setFieldToDelete}
                    />
                  </Flex>
                ),
                [PRACTICE_SLUG.CROPS]: (
                  <Flex vertical className="all-fields-data-table-view-clickable-cell" onClick={openCropDetails}>
                    <Flex align="center" gap="0.25rem">
                      {isCropNameLocked && <Icon iconName="tabler/lock" size="xs" color="grey-600" className="all-fields-data-table-view-crop-lock-icon" />}
                      <PracticeTableTooltip title={cropLabel(crop.translationSlug, crop.cropName)}>
                        {({ ref }) => (
                          <div ref={ref} className="margin-0 historical-practice-data-table-view-crop-name">
                            {cropLabel(crop.translationSlug, crop.cropName)}
                          </div>
                        )}
                      </PracticeTableTooltip>
                    </Flex>
                    <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                      {entry.previousMainCrop
                        ? t("historicalPracticeDataTableView:table.data.previousCrop", {
                            cropName: cropLabel(entry.previousMainCrop.translationSlug, entry.previousMainCrop.cropName),
                          })
                        : t("historicalPracticeDataTableView:table.data.na")}
                    </Typography>
                  </Flex>
                ),
                growingPeriod:
                  crop.seedingStartDate && crop.harvestEndDate ? (
                    <PracticeChip
                      practiceName={`${moment(crop.seedingStartDate).format("DD MMM")} – ${moment(crop.harvestEndDate).format("DD MMM")}`}
                      hasMissingData={isMissingDataTracked && !completeness.growingPeriod}
                      isEditable={missingItem.isGrowingPeriodEditable}
                      onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.CROPS, true)}
                    />
                  ) : (
                    <Button
                      type="text"
                      className="historical-practice-data-table-view-add-practice-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddMissingItemClicked(missingItem, PRACTICE_SLUG.CROPS, false);
                      }}
                    >
                      {t("historicalPracticeDataTableView:table.data.actions.add")}
                    </Button>
                  ),
                [PRACTICE_SLUG.SEEDINGS]: applicability.seedings ? (
                  <PracticeColumn
                    hideMissingData={isBeforeMandatoryYears}
                    practices={seedings}
                    practicesData={seedingPractices}
                    getPracticeId={(id) => id}
                    getItemKey={(id) => id}
                    isExpanded={!!expandedPracticeColumns[PRACTICE_SLUG.SEEDINGS]}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.SEEDINGS, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.SEEDINGS, false)}
                    isEditable={missingItem.isSeedingEditable}
                    practiceType={PRACTICE_SLUG.SEEDINGS}
                  />
                ) : (
                  naCell
                ),
                [PRACTICE_SLUG.TILLAGES]: applicability.tillages ? (
                  <PracticeColumn
                    hideMissingData={!missingPracticeColumns[PRACTICE_SLUG.TILLAGES]}
                    practices={tillages}
                    practicesData={tillagePractices}
                    getPracticeId={(tillage) => tillage.assignedPracticeId}
                    getItemKey={(tillage) => tillage.id || `tillage-${tillage.assignedPracticeId}`}
                    isExpanded={!!expandedPracticeColumns[PRACTICE_SLUG.TILLAGES]}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.TILLAGES, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.TILLAGES, false)}
                    isEditable={missingItem.isTillageEditable || missingItem.isTillageDepthEditable || missingItem.areTillageDatesEditable}
                    practiceType={PRACTICE_SLUG.TILLAGES}
                    isTillageDepthRequired={entry.isTillageDepthRequired}
                  />
                ) : (
                  naCell
                ),
                [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: applicability.organicFertilizers ? (
                  <PracticeColumn
                    hideMissingData={!missingPracticeColumns[PRACTICE_SLUG.ORGANIC_FERTILIZERS]}
                    practices={organicFertilizers}
                    practicesData={organicFertilizerPractices}
                    getPracticeId={(organicFertilizer) => organicFertilizer.assignedPracticeId}
                    getItemKey={(organicFertilizer) => organicFertilizer.id || `organic-${organicFertilizer.assignedPracticeId}`}
                    isExpanded={!!expandedPracticeColumns[PRACTICE_SLUG.ORGANIC_FERTILIZERS]}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.ORGANIC_FERTILIZERS, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.ORGANIC_FERTILIZERS, false)}
                    isEditable={!!missingItem.isOrganicEditable || !!missingItem.areOrganicDatesEditable}
                    practiceType={PRACTICE_SLUG.ORGANIC_FERTILIZERS}
                  />
                ) : (
                  naCell
                ),
                [PRACTICE_SLUG.MINERAL_FERTILIZERS]: applicability.mineralFertilizers ? (
                  <Tooltip
                    title={isMineralFertilizerSaveBlocked ? t("allFieldsDataTableView:table.data.mineralFertilizersBlocked") : undefined}
                    style={{ minWidth: 0, maxWidth: "100%" }}
                  >
                    <PracticeColumn
                      hideMissingData={isBeforeMandatoryYears}
                      practices={mineralFertilizers}
                      practicesData={mineralFertilizerPractices}
                      getPracticeId={(mineralFertilizer) => mineralFertilizer.assignedPracticeId}
                      getItemKey={(mineralFertilizer) => mineralFertilizer.id || `mineral-${mineralFertilizer.assignedPracticeId}`}
                      isExpanded={!!expandedPracticeColumns[PRACTICE_SLUG.MINERAL_FERTILIZERS]}
                      onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.MINERAL_FERTILIZERS, true)}
                      onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.MINERAL_FERTILIZERS, false)}
                      isEditable={!!missingItem.isMineralEditable || !!missingItem.areMineralDatesEditable}
                      disabled={isMineralFertilizerSaveBlocked}
                      practiceType={PRACTICE_SLUG.MINERAL_FERTILIZERS}
                    />
                  </Tooltip>
                ) : (
                  naCell
                ),
                yield: applicability.yield ? (
                  typeof crop.yieldPerHa === "number" ? (
                    <PracticeChip
                      practiceName={`${formatNumber({ number: crop.yieldPerHa })} ${getUnitDisplayLabel(crop.yieldPerHaUnit)}`}
                      hasMissingData={isMissingDataTracked && !completeness.yield}
                      isEditable={!!missingItem.isYieldEditable}
                      onClick={() => onAddMissingItemClicked(missingItem, YIELD_PSEUDO_PRACTICE_TYPE, true)}
                    />
                  ) : (
                    <Button
                      type="text"
                      className="historical-practice-data-table-view-add-practice-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddMissingItemClicked(missingItem, YIELD_PSEUDO_PRACTICE_TYPE, false);
                      }}
                    >
                      {t("historicalPracticeDataTableView:table.data.actions.add")}
                    </Button>
                  )
                ) : (
                  naCell
                ),
                status: (
                  <Flex
                    align="center"
                    gap="0.5rem"
                    className="historical-practice-data-table-view-status"
                    style={missingRequiredPracticesCount > 0 ? { cursor: "pointer" } : undefined}
                    onClick={handleStatusClick}
                  >
                    {(completeness.isComplete || isBeforeMandatoryYears) && missingRequiredPracticesCount === 0 && (
                      <span className="all-fields-data-table-view-status-dot-complete">
                        <span className="all-fields-data-table-view-status-dot-complete-inner" />
                      </span>
                    )}
                    {missingRequiredPracticesCount > 0 && <CounterBadge count={missingRequiredPracticesCount} />}
                  </Flex>
                ),
              };
            })
            .filter((entry) => entry !== null)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedYear,
      filteredEntries,
      fields,
      expandedPracticeColumns,
      selectedItems,
      selectedCropType,
      carbonProgram,
      practiceCrops,
      t,
      cropLabel,
      seedingPractices,
      tillagePractices,
      organicFertilizerPractices,
      mineralFertilizerPractices,
      onCheckBoxClicked,
      buildMissingItem,
      isMultiSelectMode,
      renderBaselineCell,
      verificationEditingRequests,
      fieldIdsWithMainCropThisYear,
      toggleHighlightMissingPractices,
      selectedRowCount,
      carbonProgramOutcomes,
    ]
  );

  const placeholderRows = useMemo(
    () =>
      placeholderFields.map((field) => {
        const key = getPlaceholderKey(field.id);
        const placeholderItem = placeholderItemsByKey.get(key);
        const isSelected = selectedItems.has(key);
        const isCheckboxDisabled = !isSelected && (!placeholderItem || isItemLockedOut(placeholderItem));

        // a placeholder has no crop at all, so every base practice is trivially missing — matches the base
        // count real rows use before the optional mineral fertilizer bundle is added on top
        const isEnrolled = isFieldEnrolled(allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [], carbonProgram);
        const isMandatoryYear =
          isEnrolled &&
          isPracticeMandatoryForYear(
            PRACTICE_SLUG.CROPS,
            allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [],
            carbonProgram,
            activeFarmReportingYear,
            selectedYear
          );

        return {
          key,
          fieldId: field.id,
          isPlaceholderRow: true,
          isCheckboxDisabledByCropTypeLock: isCheckboxDisabled,
          status: isMandatoryYear ? (
            <Flex
              align="center"
              gap="0.5rem"
              className="historical-practice-data-table-view-status"
              style={{ cursor: "pointer" }}
              onClick={() => openAddCropTypeDrawer([field])}
              data-testid={`all-fields-data-table-view-placeholder-status-${field.id}`}
            >
              <CounterBadge count={5} />
            </Flex>
          ) : undefined,
          select: (
            <Flex align="center" justify="flex-start" className="all-fields-data-table-view-select-checkbox-wrapper" data-no-row-select>
              <Checkbox
                className="historical-practice-data-table-view-select-checkbox"
                checked={isSelected}
                disabled={isCheckboxDisabled}
                onChange={(e) => onCheckBoxClicked(key, !!e.nativeEvent.shiftKey)}
              />
            </Flex>
          ),
          field: (
            <TableFieldCell
              field={field}
              carbonProgram={carbonProgram}
              availablePracticeBundles={allFieldsPractices?.[field.id]?.availablePracticeBundles ?? []}
              year={selectedYear}
              activeFarmReportingYear={activeFarmReportingYear}
              hasNoPayoutDueToNoPoints={isFieldWithoutPayoutDueToNoPoints(carbonProgramOutcomes?.[field.id], activeFarmReportingYear)}
              onAddCrop={() => openAddCropTypeDrawer([field])}
              addCropDisabledReason={addCropDisabledReason}
              hasAnyRowSelected={selectedRowCount > 0}
              hideHoverAddCropButton
              onEditField={setFieldToEdit}
              onDeleteField={setFieldToDelete}
            />
          ),
          [PRACTICE_SLUG.CROPS]: (
            <Flex justify="flex-start" align="center">
              <Tooltip title={addCropDisabledReason}>
                <Button
                  type="text"
                  size="small"
                  className="all-fields-data-table-view-add-crop-button"
                  disabled={!!addCropDisabledReason}
                  onClick={() => openAddCropTypeDrawer([field])}
                >
                  {t("allFieldsDataTableView:placeholders.addMainCrop")}
                </Button>
              </Tooltip>
            </Flex>
          ),
          baseline: renderBaselineCell(field),
        };
      }),
    [
      placeholderFields,
      placeholderItemsByKey,
      selectedItems,
      isItemLockedOut,
      onCheckBoxClicked,
      t,
      carbonProgram,
      allFieldsPractices,
      addCropDisabledReason,
      renderBaselineCell,
      activeFarmReportingYear,
      selectedYear,
      selectedRowCount,
      carbonProgramOutcomes,
    ]
  );

  // interleaved in the same field-sort order as the real rows (via fieldOrderIndex) rather than appended at the
  // end, so the table still reads as one consistently-ordered list regardless of the applied field sort
  const combinedTableData = useMemo(
    () =>
      [...tableData, ...placeholderRows]
        .map((row) => ({ row, orderIndex: fieldOrderIndex.get(row.fieldId) ?? Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(({ row }) => row),
    [tableData, placeholderRows, fieldOrderIndex]
  );

  // distinct fields actually shown right now (crop/missing-type/status filters narrow this to fewer fields than
  // the farm total, since they operate on crop entries rather than the field list itself)
  const visibleFieldIds = useMemo(() => new Set(combinedTableData.map((row) => row.fieldId)), [combinedTableData]);
  const visibleFieldsCount = useMemo(() => visibleFieldIds.size, [visibleFieldIds]);

  const visibleEnrolledFields = useMemo(
    () =>
      getEnrolledFields(
        (fields ?? []).filter((field) => visibleFieldIds.has(field.id)),
        carbonProgramOutcomes ?? {},
        carbonProgram,
        allFieldsPractices
      ),
    [fields, visibleFieldIds, carbonProgramOutcomes, carbonProgram, allFieldsPractices]
  );
  const visibleEnrolledFieldsTotalHectares = useMemo(
    () => visibleEnrolledFields.reduce((total, field) => total + (fieldSizes[field.id] ?? 0), 0),
    [visibleEnrolledFields, fieldSizes]
  );

  const visibleFieldsTotalHectares = useMemo(
    () => Array.from(visibleFieldIds).reduce((total, fieldId) => total + (fieldSizes[fieldId] ?? 0), 0),
    [visibleFieldIds, fieldSizes]
  );

  const visibleFieldsSizeSummary = useMemo(() => {
    if (!allFieldsTotalHectares) return null;

    const sharePercent = Math.round((visibleFieldsTotalHectares / allFieldsTotalHectares) * 100);

    return t("allFieldsDataTableView:table.header.fieldsSizeSummary", {
      size: formatNumber({ number: visibleFieldsTotalHectares, decimalPlaces: 1 }),
      percent: sharePercent,
    });
  }, [allFieldsTotalHectares, visibleFieldsTotalHectares, t]);

  // a practice column only gets an expand toggle once some row actually has more than one entry to hide
  const expandableColumnSlugs = [PRACTICE_SLUG.SEEDINGS, PRACTICE_SLUG.TILLAGES, PRACTICE_SLUG.ORGANIC_FERTILIZERS, PRACTICE_SLUG.MINERAL_FERTILIZERS];
  const expandableColumns = useMemo(
    () =>
      Object.fromEntries(
        expandableColumnSlugs.map((practiceSlug) => [
          practiceSlug,
          {
            hasMultiple: filteredEntries.some((entry) => (entry.assignedPractices[practiceSlug]?.length ?? 0) > 1),
            isExpanded: !!expandedPracticeColumns[practiceSlug],
            onToggleExpand: () => toggleExpandColumn(practiceSlug),
          },
        ])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredEntries, expandedPracticeColumns, toggleExpandColumn]
  );

  const tableColumns = useMemo(
    () =>
      getAllFieldsDataTableColumns(t, {
        isSelectAllChecked,
        onSelectAllClicked: handleSelectAllOfFirstType,
        showMineralFertilizers: isMineralFertilizerOfferedByProgram(carbonProgram) || hasAnyMineralFertilizerDataForYear,
        showBaselineColumn,
        visibleFieldsCount,
        totalFieldsCount,
        enrolledFieldsCount: visibleEnrolledFields.length,
        enrolledFieldsTotalHectares: visibleEnrolledFieldsTotalHectares,
        fieldsSizeSummary: visibleFieldsSizeSummary,
        hasFiltersActive: !!hasFiltersActive,
        expandableColumns,
        highlightedMissingRowKey,
        isEnrollmentDataLoading: !!isEnrollmentDataLoading,
      }),
    [
      t,
      isSelectAllChecked,
      handleSelectAllOfFirstType,
      carbonProgram,
      hasAnyMineralFertilizerDataForYear,
      showBaselineColumn,
      visibleFieldsCount,
      totalFieldsCount,
      visibleEnrolledFields,
      visibleEnrolledFieldsTotalHectares,
      visibleFieldsSizeSummary,
      hasFiltersActive,
      expandableColumns,
      highlightedMissingRowKey,
      isEnrollmentDataLoading,
    ]
  );

  // "max-content" scroll.x makes antd size columns by content instead of respecting each column's own `width`
  // (needed for the fixed-left columns to work at all) — summing the explicit widths keeps them as authored; virtual mode
  // adds the widths up itself, so they must be pixel numbers rather than rem strings
  const tableColumnsWithPixelWidths = useMemo(
    () => tableColumns.map((column) => ({ ...column, width: typeof column.width === "string" ? parseFloat(column.width) * 16 : Number(column.width) || 0 })),
    [tableColumns]
  );
  const tableScrollWidth = useMemo(
    () => tableColumnsWithPixelWidths.reduce((totalWidthInPixels, column) => totalWidthInPixels + column.width, 0),
    [tableColumnsWithPixelWidths]
  );

  const handleAddCropTypeContinue = (chosenYear: number, cropType: CROP_TYPE_FCPO, eligibleFields: FieldListField[]) => {
    // fieldsForAddCropFlow stays set for the multi-field case, so "Back" can re-show the picker
    setShowAddCropTypeDrawer(false);

    if (eligibleFields.length === 1) {
      setFieldsForAddCropFlow(null);
      const crop = NEW_CROP(cropType, `${CALENDAR_YEAR_START}${chosenYear}`, `${CALENDAR_YEAR_END}${chosenYear}`);
      setSingleCropToAdd({ field: eligibleFields[0], crop, year: chosenYear, existingTillages: [], existingFertilizers: [] });
      return;
    }

    const { getReportingYearRange } = getCurrentReportingValues(activeFarmReportingYear ?? moment().year(), carbonProgram);
    const { start, end } = getReportingYearRange(chosenYear);

    setCropsToAdd(
      eligibleFields.map((field) => ({
        // same date shape the bulk endpoint upserts on: the reporting year range bounds the crop, the inner dates
        // stay open for the drawer to collect
        ...NEW_CROP(cropType, start.toISOString(), end.toISOString()),
        id: generateRandomId(),
        year: chosenYear,
        seedingEndDate: "",
        harvestStartDate: "",
        fieldId: field.id,
        isInRedzone: field.isInRedzone,
        isMandatory: false,
        isBaseline: false,
        isPlaceholder: true,
        availablePracticeBundles: allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [],
        fertilizerManagement: [],
      }))
    );
  };

  const handleOpenCopyToPlaceholdersDrawer = () => {
    const copyData = buildCopyToPlaceholdersData(singleSelectedRealCropItem, selectedPlaceholderItems, allFieldsPractices);
    if (!copyData) return;

    setCopyTemplateCrop(copyData.templateCrop);
    setCropsToCopyToPlaceholders(copyData.targetCrops);
    setCopySourceTillages(copyData.tillages);
    setCopySourceFertilizers(copyData.fertilizers);
  };

  const resetCopyToPlaceholdersState = () => {
    setCropsToCopyToPlaceholders(null);
    setCopyTemplateCrop(null);
    setCopySourceTillages([]);
    setCopySourceFertilizers([]);
  };

  const resetAddCropsState = () => {
    setCropsToAdd(null);
    setFieldsForAddCropFlow(null);
  };

  const getAffectedFieldsFor = (crops: BulkEditCrop[]) => (fields ?? []).filter((field) => crops.some((crop) => crop.fieldId === field.id));

  useEffect(() => {
    const selectedMissingData = Array.from(selectedItems.values());
    setAddGrowingPeriodEnabled(selectedMissingData.some((item) => item.isGrowingPeriodEditable));
    setAddSeedingEnabled(selectedMissingData.some((item) => item.isSeedingEditable));
    setAddTillageEnabled(selectedMissingData.some((item) => item.isTillageEditable || item.isTillageDepthEditable));
    setAddOrganicFertilizerEnabled(selectedMissingData.some((item) => item.isOrganicEditable));
    setAddMineralFertilizerEnabled(selectedMissingData.some((item) => item.isMineralEditable));
    setAddYieldEnabled(selectedMissingData.some((item) => item.isYieldEditable));
  }, [selectedItems]);

  const isEmptyForSelectedYear = selectedYear && combinedTableData.length === 0;

  if (isEmptyForSelectedYear && hasFiltersActive && onClearFilters) {
    return (
      <Flex justify="center" align="center" className="historical-practice-data-table-view-no-matches-empty-state-container">
        <NoMatchesFeedback handleClearFilters={onClearFilters} />
      </Flex>
    );
  }

  if (isEmptyForSelectedYear) {
    return (
      <Flex justify="center" align="center" className="historical-practice-data-table-view-no-matches-empty-state-container">
        <Typography variant="paragraph-m" color="grey-600">
          {t("allFieldsDataTableView:filters.noResultsForYear")}
        </Typography>
      </Flex>
    );
  }

  // the bulk drawer picks the data point itself, so with the improvements flag on the bar just opens it. With the
  // flag off the old bar still names a practice up front and the single-practice drawer handles it
  const handleBulkEdit = (practiceType?: MissingDataDrawerPracticeType, mode: BulkDataDrawerMode = BULK_DATA_DRAWER_MODE.edit) => {
    setDrawerItems(Array.from(selectedItems.values()));
    setSelectedType(practiceType);
    setDrawerShouldValidateOnMount(true);
    setBulkDrawerMode(mode);
    setIsBulkDrawerShown(isBulkEditingImprovementsEnabled);
    setShowDrawerMissingDataDrawer(true);
  };

  const closeDrawers = () => {
    setShowDrawerMissingDataDrawer(false);
    setDrawerItems([]);
    setSelectedType(undefined);
    setIsBulkDrawerShown(false);
  };

  const selectedFields = (fields ?? []).filter((field) => Array.from(selectedItems.values()).some((item) => item.fieldId === field.id));

  const handleDeleteSelectedCrops = async () => {
    setShowDeleteCropsModal(false);
    try {
      await deleteFieldCropPractices(Array.from(selectedItems.values()).map((item) => ({ fieldId: item.fieldId, cropId: item.key })));
      message({ content: t("fieldTimeline:multiSelection.deleteCropsSuccess"), icon: "tick" });
      handleClearSelection();
    } catch {
      message({ content: t("fieldTimeline:multiSelection.deleteCropsError"), icon: "error" });
    }
  };

  const handleClearSelection = () => {
    setSelectedItems(new Map());
    setIsMultiSelectMode(false);
  };

  const handleCropsSkipped = (skippedCropIds: string[]) => {
    setSelectedItems((previouslySelectedItems) => narrowSelectionToSkippedCrops(previouslySelectedItems, skippedCropIds));
    setIsMultiSelectMode(true);
  };

  return selectedYear ? (
    <>
      <Table
        className="historical-practice-data-table-view-table-grid"
        columns={tableColumnsWithPixelWidths}
        data={combinedTableData}
        // virtual needs a numeric scroll.y; the viewport height stands in until the real body height is measured, so the first
        // render doesn't mount every row
        virtual
        scroll={{ x: tableScrollWidth, y: tableBodyHeight || window.innerHeight }}
        // the virtual table ignores rowClassName
        onRow={getGroupedRowProps}
      />
      {drawerItems.length > 0 && isBulkDrawerShown && (
        <BulkDataDrawer
          showDrawer={showDrawerMissingDataDrawer}
          missingDataFormItems={drawerItems}
          onClose={closeDrawers}
          selectedYear={selectedYear}
          mode={bulkDrawerMode}
          availablePracticeTypes={availablePracticeTypes}
          onCropsSkipped={handleCropsSkipped}
        />
      )}
      {drawerItems.length > 0 && selectedType && !isBulkDrawerShown && (
        <AddEditMissingDataDrawer
          showDrawer={showDrawerMissingDataDrawer}
          missingDataFormItems={drawerItems}
          onClose={closeDrawers}
          practiceType={selectedType}
          selectedYear={selectedYear}
          shouldValidateOnMount={drawerShouldValidateOnMount}
          onCropsSkipped={handleCropsSkipped}
        />
      )}
      {isMultiSelectMode &&
        selectedRowCount > 0 &&
        (isBulkEditingImprovementsEnabled ? (
          <BulkActionBar
            selectedCount={selectedRowCount}
            selectedFieldIds={Array.from(selectedItems.values()).map((item) => item.fieldId)}
            onClear={handleClearSelection}
            hideSelectionCount={isCopyToPlaceholdersSelection}
            selectionLabel={
              isCopyToPlaceholdersSelection
                ? t("fieldTimeline:multiSelection.copyingCropToPlaceholders", {
                    cropName: cropLabel(singleSelectedRealCropItem!.crop!.translationSlug, singleSelectedRealCropItem!.crop!.cropName),
                    count: selectedPlaceholderItems.length,
                  })
                : isPlaceholderOnlySelection
                ? t("fieldTimeline:multiSelection.fieldsToAddCropTo", { count: selectedRowCount })
                : undefined
            }
            copyLabel={
              isCopyToPlaceholdersSelection ? t("fieldTimeline:multiSelection.copyToPlaceholdersButton", { count: selectedPlaceholderItems.length }) : undefined
            }
            copyTooltip={isCopyToPlaceholdersSelection ? t("fieldTimeline:multiSelection.copyToPlaceholdersTooltip") : undefined}
            onCopy={isCopyToPlaceholdersSelection ? handleOpenCopyToPlaceholdersDrawer : undefined}
            addCropsLabel={!isCopyToPlaceholdersSelection && isPlaceholderOnlySelection ? t("fieldTimeline:multiSelection.addCrops") : undefined}
            onAddData={isCopyToPlaceholdersSelection ? undefined : () => handleBulkEdit(undefined, BULK_DATA_DRAWER_MODE.add)}
            onEditData={isCopyToPlaceholdersSelection ? undefined : () => handleBulkEdit(undefined, BULK_DATA_DRAWER_MODE.edit)}
            onEnrollFields={isCopyToPlaceholdersSelection ? undefined : setFieldIdsToEnroll}
            isEnrollFieldsDisabled={isDataSubmitted && !isDataSubmissionReopened}
            onAddCrops={isCopyToPlaceholdersSelection ? undefined : () => openAddCropTypeDrawer(selectedFields)}
            addCropsDisabledReason={addCropDisabledReason}
            onDeleteCrops={isCopyToPlaceholdersSelection ? undefined : () => setShowDeleteCropsModal(true)}
            isDeleteCropsDisabled={isPlaceholderOnlySelection}
          />
        ) : (
          <MultiCropSelectionBarMissingData
            selectedCount={selectedRowCount}
            onEdit={handleBulkEdit}
            onClear={handleClearSelection}
            addGrowingPeriodEnabled={addGrowingPeriodEnabled}
            addSeedingEnabled={addSeedingEnabled}
            addTillageEnabled={addTillageEnabled}
            addOrganicFertilizerEnabled={addOrganicFertilizerEnabled}
            showAddMineralFertilizerButton
            addMineralFertilizerEnabled={addMineralFertilizerEnabled}
            showAddYieldButton
            addYieldEnabled={addYieldEnabled}
          />
        ))}

      <Modal
        showModal={showDeleteCropsModal}
        onOk={handleDeleteSelectedCrops}
        onCancel={() => setShowDeleteCropsModal(false)}
        okText={t("fieldTimeline:multiSelection.deleteCropsConfirm")}
        cancelText={t("allFieldView:cropForm.cropDeletionModal.cancel")}
        content={
          <>
            <Typography variant="h3">{t("fieldTimeline:multiSelection.deleteCropsTitle")}</Typography>
            <Typography variant="paragraph-m">{t("fieldTimeline:multiSelection.deleteCropsDescription", { count: selectedRowCount })}</Typography>
          </>
        }
      />
      {fieldToEdit && (
        <Drawer
          className="field-details-drawer"
          title={t("farm:fieldsTab.fieldDetails")}
          visible
          onClose={() => setFieldToEdit(null)}
          styles={{ body: { padding: "0 2rem" } }}
        >
          <ModalProvider>
            <FieldDetails fieldId={fieldToEdit.id} setShowFieldEditDrawer={() => setFieldToEdit(null)} />
          </ModalProvider>
        </Drawer>
      )}
      {fieldToDelete && (
        <Modal
          showModal
          onOk={() => {
            deleteField({ fieldId: fieldToDelete.id });
            setFieldToDelete(null);
          }}
          onCancel={() => setFieldToDelete(null)}
          okText={t("farm:deleteModalConfirm.deleteText")}
          cancelText={t("farm:deleteModalConfirm.cancel")}
          title={t("farm:deleteModalConfirm.fieldTitle", { fieldName: fieldToDelete.name })}
          content={<Typography variant="paragraph-m">{t("farm:deleteModalConfirm.fieldMessage", { fieldName: fieldToDelete.name })}</Typography>}
        />
      )}
      {fieldIdsToEnroll && (
        <FieldEnrollmentDrawer
          fields={(fields ?? []).filter((field) => fieldIdsToEnroll.includes(field.id))}
          visible={fieldIdsToEnroll.length > 0}
          onClose={() => setFieldIdsToEnroll(null)}
        />
      )}
      {fieldsForAddCropFlow && (
        <AddCropTypeDrawer
          visible={showAddCropTypeDrawer}
          onClose={() => {
            setShowAddCropTypeDrawer(false);
            setFieldsForAddCropFlow(null);
          }}
          fields={fieldsForAddCropFlow}
          fieldPractices={allFieldsPractices ?? {}}
          year={selectedYear ?? activeFarmReportingYear ?? moment().year()}
          availableYears={addCropYearOptions}
          // real-crop selections lock out their own type, offering only the complementary type
          onContinue={handleAddCropTypeContinue}
        />
      )}
      {singleCropToAdd && (
        <AddEditCropDrawer
          availablePracticeBundles={allFieldsPractices?.[singleCropToAdd.field.id]?.availablePracticeBundles ?? []}
          allCrops={allFieldsPractices?.[singleCropToAdd.field.id]?.crops ?? []}
          existingCrop={singleCropToAdd.crop}
          year={singleCropToAdd.year}
          existingFertilizers={singleCropToAdd.existingFertilizers}
          existingTillages={singleCropToAdd.existingTillages}
          field={singleCropToAdd.field}
          showDrawer
          setShowDrawer={(show) => {
            if (!show) {
              setSingleCropToAdd(null);
              setFieldsForAddCropFlow(null);
            }
          }}
          fertilizerManagements={allFieldsPractices?.[singleCropToAdd.field.id]?.fertilizerManagements ?? []}
          viewType={VIEW_TYPE.ALL_FIELDS}
          farmId={farmId}
          carbonProgram={carbonProgram}
        />
      )}
      {/* The crops picked above are empty placeholders, so the bulk drawer fills them in like any other selection */}
      {cropsToAdd && cropsToAdd.length > 0 && (
        <BulkEditCropDrawer
          selectedCrops={cropsToAdd}
          allCrops={allFieldsPractices ?? {}}
          affectedFields={getAffectedFieldsFor(cropsToAdd)}
          selectedTillages={[]}
          selectedFertilizers={[]}
          showDrawer
          setShowDrawer={(show) => {
            if (!show) resetAddCropsState();
          }}
          onFinish={() => {
            resetAddCropsState();
            handleClearSelection();
          }}
          onBack={() => {
            setCropsToAdd(null);
            setShowAddCropTypeDrawer(true);
          }}
        />
      )}
      {cropsToCopyToPlaceholders && cropsToCopyToPlaceholders.length > 0 && copyTemplateCrop && (
        <BulkEditCropDrawer
          selectedCrops={cropsToCopyToPlaceholders}
          templateCrop={copyTemplateCrop}
          allCrops={allFieldsPractices ?? {}}
          affectedFields={getAffectedFieldsFor(cropsToCopyToPlaceholders)}
          selectedTillages={copySourceTillages}
          selectedFertilizers={copySourceFertilizers}
          showDrawer
          setShowDrawer={(show) => {
            if (!show) resetCopyToPlaceholdersState();
          }}
          onFinish={() => {
            resetCopyToPlaceholdersState();
            handleClearSelection();
          }}
        />
      )}
    </>
  ) : null;
};

// the page re-renders on each outcomes chunk and filter-bar change, which leave most of the table's props untouched
export const AllFieldsDataTable = memo(AllFieldsDataTableComponent);
